import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/**
 * True when the visitor has asked for reduced motion. The CSS in marketing.css
 * already stills every transition under the same media query; this is for the
 * behaviours CSS cannot reach — auto-cycling, reveal observers, the slide of
 * the health-check panel — which must not run at all rather than run quickly.
 *
 * Same shape as the hook in src/components/member/BiomarkerRangeBar.tsx; kept
 * separate so the marketing module has no portal imports. Safe in jsdom, which
 * has no matchMedia.
 */
export default function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(QUERY);
    const onChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    setReduced(mql.matches);
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return reduced;
}
